export type ProxyTemplateCategory =
  | "sales"
  | "recruiting"
  | "real_estate"
  | "customer_research"
  | "business"
  | "research"
  | "general"
  | "coaching_advisory";

export type ProxyRole = "session_assistant" | "guided_coach" | "custom";

export const PROXY_ROLES: ProxyRole[] = ["session_assistant", "guided_coach", "custom"];

export const PROXY_ROLE_LABELS: Record<ProxyRole, string> = {
  session_assistant: "Session Assistant",
  guided_coach: "Guided Coach",
  custom: "Custom",
};

export function isProxyRole(value: unknown): value is ProxyRole {
  return value === "session_assistant" || value === "guided_coach" || value === "custom";
}

export type ProxyTemplate = {
  id: string;
  slug: string;
  name: string;
  category: ProxyTemplateCategory;
  description: string;
  icon: string;
  openingMessage: string;
  objective: string;
  systemInstructions: string;
  suggestedQuestions: string[];
  requiredQuestions: string[];
  extractFields: string[];
  closingMessage: string;
  recommendedDurationMinutes: number;
  allowDynamicFollowups: boolean;
  allowRespondentQuestions: boolean;
  shareMessage: string;
  followUpInstructions?: string;
  proxyRole?: ProxyRole;
  isFeatured: boolean;
  isActive: boolean;
};

export const TEMPLATE_CATEGORY_LABELS: Record<ProxyTemplateCategory, string> = {
  sales: "Sales",
  recruiting: "Recruiting",
  real_estate: "Real Estate",
  customer_research: "Customer Research",
  business: "Business",
  research: "Research",
  general: "General",
  coaching_advisory: "Coaching & Advisory",
};

export const TEMPLATE_CATEGORIES = Object.keys(
  TEMPLATE_CATEGORY_LABELS,
) as ProxyTemplateCategory[];

const AI_OPENING =
  "Hi, I'm an AI Proxy created by {creatorName}. They asked me to collect a few details before you speak with them.";

const COACHING_OPENING =
  "Hi, I'm an AI Proxy created by {creatorName}.";

const COACHING_SESSION_ASSISTANT_INSTRUCTIONS =
  "Collect and clarify information for the human coach. Ask structured questions and useful follow-ups. Help the client organize progress, obstacles, decisions, and session priorities. Do not diagnose or infer hidden motivations. Do not give substantive coaching advice unless needed for clarification. Do not give HR, legal, or employment-law advice.";

const COACHING_CUSTOM_INTAKE_INSTRUCTIONS =
  "Conduct the check-in process the creator defines. Ask clear follow-up questions when answers are vague. Keep the conversation focused on gathering information for the human coach.";

function tpl(
  partial: Omit<ProxyTemplate, "id" | "isActive"> & { isActive?: boolean },
): ProxyTemplate {
  return {
    ...partial,
    id: partial.slug,
    isActive: partial.isActive ?? true,
  };
}

export const PROXY_TEMPLATES: ProxyTemplate[] = [
  // Sales
  tpl({
    slug: "lead-qualification",
    name: "Lead Qualification",
    category: "sales",
    description: "Determine whether the lead is worth a live follow-up.",
    icon: "solar:target-linear",
    openingMessage: `${AI_OPENING} I'll ask a few quick questions about your needs and timeline.`,
    objective: "Determine whether the lead is worth a live follow-up.",
    systemInstructions:
      "Qualify the lead efficiently. Confirm need, budget signal, timeline, and decision authority before wrapping up.",
    suggestedQuestions: [
      "What problem are you trying to solve?",
      "What is your timeline for making a decision?",
      "Who else is involved in the decision?",
    ],
    requiredQuestions: [
      "What is your name and company?",
      "What need are you trying to address?",
      "Do you have a budget range in mind?",
      "What is your timeline?",
      "Who has authority to make the purchase decision?",
      "What solution are you using today?",
      "How urgent is this for you?",
    ],
    extractFields: [
      "name",
      "company",
      "need",
      "budget",
      "timeline",
      "decision authority",
      "current solution",
      "urgency",
      "next step",
    ],
    closingMessage:
      "Thanks — I have what I need. {creatorName} will review this and follow up with you soon.",
    recommendedDurationMinutes: 8,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Before we schedule a call, please have a quick conversation with my AI assistant. It will ask a few qualification questions so I can prepare the right next step for you.",
    isFeatured: true,
  }),
  tpl({
    slug: "discovery-interview",
    name: "Discovery Interview",
    category: "sales",
    description: "Understand the prospect's goals, pain points, and buying process.",
    icon: "solar:chat-round-dots-linear",
    openingMessage: `${AI_OPENING} I'd like to learn more about your goals and challenges.`,
    objective: "Understand the prospect's goals, pain points, and buying process.",
    systemInstructions:
      "Run a consultative discovery conversation. Listen for pain, impact, and buying signals.",
    suggestedQuestions: [
      "What prompted you to explore options now?",
      "What does success look like for you?",
    ],
    requiredQuestions: [
      "What is your primary goal?",
      "What is the biggest challenge today?",
      "What have you tried so far?",
      "What would an ideal solution look like?",
    ],
    extractFields: ["goal", "pain point", "current approach", "ideal outcome", "timeline"],
    closingMessage: "Thank you. {creatorName} will review your answers and reach out.",
    recommendedDurationMinutes: 10,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "I'd like to understand your needs better before our next conversation. Please complete this short AI interview — it only takes a few minutes.",
    isFeatured: false,
  }),
  tpl({
    slug: "demo-follow-up",
    name: "Demo Follow-up",
    category: "sales",
    description: "Capture feedback and next steps after a product demo.",
    icon: "solar:monitor-linear",
    openingMessage: `${AI_OPENING} I'd like to hear your thoughts after the demo.`,
    objective: "Capture demo feedback, objections, and next steps.",
    systemInstructions: "Confirm what resonated, what was unclear, and what would unblock a next step.",
    suggestedQuestions: ["What stood out most from the demo?", "What questions remain?"],
    requiredQuestions: [
      "Did the demo address your main use case?",
      "What concerns or objections do you have?",
      "What would you need to see before moving forward?",
    ],
    extractFields: ["fit assessment", "objections", "next step", "decision timeline"],
    closingMessage: "Thanks for the feedback. {creatorName} will follow up shortly.",
    recommendedDurationMinutes: 8,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Thanks for joining the demo. Please share a few quick follow-up thoughts with my AI assistant so I can prepare the right next step.",
    isFeatured: false,
  }),
  tpl({
    slug: "customer-needs-assessment",
    name: "Customer Needs Assessment",
    category: "sales",
    description: "Assess requirements before proposing a solution.",
    icon: "solar:clipboard-check-linear",
    openingMessage: `${AI_OPENING} I'll ask about your requirements so we can recommend the right fit.`,
    objective: "Assess customer requirements before proposing a solution.",
    systemInstructions: "Gather must-haves, nice-to-haves, constraints, and success criteria.",
    suggestedQuestions: ["What are your must-have requirements?", "Any hard constraints?"],
    requiredQuestions: [
      "What problem must be solved?",
      "What are your must-have features?",
      "What constraints should we know about (budget, timeline, integrations)?",
    ],
    extractFields: ["must-haves", "constraints", "timeline", "success criteria"],
    closingMessage: "Thank you. {creatorName} will use this to prepare recommendations.",
    recommendedDurationMinutes: 10,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Before I put together options, please answer a few questions with my AI assistant about your requirements.",
    isFeatured: false,
  }),

  // Recruiting
  tpl({
    slug: "candidate-pre-screening",
    name: "Candidate Pre-Screening",
    category: "recruiting",
    description: "Screen candidates before a live interview.",
    icon: "solar:user-check-linear",
    openingMessage: `${AI_OPENING} I'll ask a few screening questions about your experience and availability.`,
    objective: "Screen candidates for role fit before a live interview.",
    systemInstructions:
      "Be professional and efficient. Confirm experience, skills, availability, and salary expectations.",
    suggestedQuestions: [
      "Tell me about your current role.",
      "What relevant skills do you bring to this role?",
    ],
    requiredQuestions: [
      "What is your current role?",
      "How many years of relevant experience do you have?",
      "What are your key skills for this role?",
      "What is your employment status?",
      "When could you start?",
      "What are your salary expectations?",
      "What work arrangement do you prefer?",
      "Where are you located?",
      "Why are you interested in this role?",
    ],
    extractFields: [
      "current role",
      "years of experience",
      "relevant skills",
      "employment status",
      "availability",
      "salary expectation",
      "work arrangement preference",
      "location",
      "reason for applying",
      "major qualifications",
    ],
    closingMessage:
      "Thank you for your time. {creatorName} will review your answers and follow up if there's a fit.",
    recommendedDurationMinutes: 15,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Thanks for applying. Please complete this short AI interview before the next step. It should only take a few minutes.",
    isFeatured: true,
  }),
  tpl({
    slug: "candidate-experience-interview",
    name: "Candidate Experience Interview",
    category: "recruiting",
    description: "Collect detailed experience and project stories.",
    icon: "solar:case-round-linear",
    openingMessage: `${AI_OPENING} I'd like to learn more about your experience and recent projects.`,
    objective: "Collect detailed candidate experience and project examples.",
    systemInstructions: "Ask for specific examples and measurable outcomes.",
    suggestedQuestions: ["Describe a recent project you're proud of.", "What was your role on the team?"],
    requiredQuestions: [
      "Walk me through your most relevant recent role.",
      "Describe a challenging project and how you handled it.",
      "What tools or technologies do you use most?",
    ],
    extractFields: ["experience summary", "project examples", "tools", "strengths"],
    closingMessage: "Thanks — {creatorName} will review and be in touch.",
    recommendedDurationMinutes: 30,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Before we schedule a live interview, please share a bit more about your experience with my AI assistant.",
    isFeatured: false,
  }),
  tpl({
    slug: "reference-interview",
    name: "Reference Interview",
    category: "recruiting",
    description: "Collect structured reference feedback.",
    icon: "solar:users-group-two-rounded-linear",
    openingMessage: `${AI_OPENING} I'm gathering a brief reference about a candidate.`,
    objective: "Collect structured reference feedback about a candidate.",
    systemInstructions: "Be respectful. Confirm relationship, strengths, and areas for development.",
    suggestedQuestions: ["How do you know the candidate?", "How would you rate their performance?"],
    requiredQuestions: [
      "What is your relationship to the candidate?",
      "How long did you work together?",
      "What are their top strengths?",
      "Would you hire or work with them again?",
    ],
    extractFields: ["relationship", "duration", "strengths", "weaknesses", "rehire recommendation"],
    closingMessage: "Thank you for providing this reference.",
    recommendedDurationMinutes: 8,
    allowDynamicFollowups: true,
    allowRespondentQuestions: false,
    shareMessage:
      "I'm collecting a brief reference. Please answer a few questions with my AI assistant — it should only take a few minutes.",
    isFeatured: false,
  }),
  tpl({
    slug: "job-applicant-qualification",
    name: "Job Applicant Qualification",
    category: "recruiting",
    description: "Verify basic qualifications and interest.",
    icon: "solar:document-add-linear",
    openingMessage: `${AI_OPENING} I'll confirm a few details about your application.`,
    objective: "Verify basic applicant qualifications and interest.",
    systemInstructions: "Confirm eligibility, work authorization, and role fit basics.",
    suggestedQuestions: ["Are you authorized to work in this location?", "Why does this role interest you?"],
    requiredQuestions: [
      "Do you meet the basic requirements listed in the job posting?",
      "Are you authorized to work in the required location?",
      "What is your expected compensation range?",
      "When could you start?",
    ],
    extractFields: ["meets requirements", "work authorization", "compensation", "start date"],
    closingMessage: "Thank you. {creatorName} will review your application.",
    recommendedDurationMinutes: 6,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Thanks for applying. Please complete this quick qualification step with my AI assistant.",
    isFeatured: false,
  }),

  // Real Estate
  tpl({
    slug: "buyer-qualification",
    name: "Buyer Qualification",
    category: "real_estate",
    description: "Qualify property buyers before arranging viewings.",
    icon: "solar:home-2-linear",
    openingMessage: `${AI_OPENING} I'll ask a few questions about your property requirements and budget.`,
    objective: "Qualify property buyers before arranging viewings.",
    systemInstructions:
      "Gather budget, financing, location preferences, timeline, and decision makers.",
    suggestedQuestions: [
      "What type of property are you looking for?",
      "What areas are you considering?",
    ],
    requiredQuestions: [
      "What is your budget range?",
      "Will you pay cash or use financing?",
      "What is your financing status?",
      "What locations are you considering?",
      "What property type do you want?",
      "What size or layout do you need?",
      "How will you use the property?",
      "When do you hope to purchase?",
      "Who else is involved in the decision?",
      "Any special requirements?",
      "Have you already viewed other properties?",
    ],
    extractFields: [
      "budget",
      "cash or financing",
      "financing status",
      "preferred location",
      "property type",
      "size requirements",
      "intended use",
      "target date",
      "decision makers",
      "special requirements",
      "properties already considered",
    ],
    closingMessage:
      "Thank you. {creatorName} will review your requirements and follow up with suitable options.",
    recommendedDurationMinutes: 15,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Before we arrange a viewing, please have a quick conversation with my AI assistant. It will ask a few questions about your property requirements and budget so I can prepare the right options for you.",
    isFeatured: true,
  }),
  tpl({
    slug: "seller-intake",
    name: "Seller Intake",
    category: "real_estate",
    description: "Collect property and seller details before listing.",
    icon: "solar:key-linear",
    openingMessage: `${AI_OPENING} I'll gather details about your property and selling plans.`,
    objective: "Collect property and seller details before listing.",
    systemInstructions: "Gather property details, pricing expectations, timeline, and title status.",
    suggestedQuestions: ["Tell me about the property.", "When are you hoping to sell?"],
    requiredQuestions: [
      "What type of property is it?",
      "Where is it located?",
      "What is the lot or floor area?",
      "What is your asking price?",
      "Who owns the property?",
      "What is the title status?",
      "Is it currently occupied?",
      "Why are you selling?",
      "What is your selling timeline?",
      "How flexible are you on price?",
      "Have you authorized a broker?",
    ],
    extractFields: [
      "property type",
      "location",
      "lot/floor area",
      "asking price",
      "ownership",
      "title status",
      "occupancy",
      "reason for selling",
      "selling timeline",
      "price flexibility",
      "broker authorization status",
    ],
    closingMessage: "Thank you. {creatorName} will review and contact you about next steps.",
    recommendedDurationMinutes: 10,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Before we discuss listing your property, please share a few details with my AI assistant so I can prepare properly.",
    isFeatured: false,
  }),
  tpl({
    slug: "property-requirements-interview",
    name: "Property Requirements Interview",
    category: "real_estate",
    description: "Understand detailed buyer or renter requirements.",
    icon: "solar:buildings-2-linear",
    openingMessage: `${AI_OPENING} I'd like to understand your property requirements in detail.`,
    objective: "Understand detailed property requirements.",
    systemInstructions: "Clarify must-haves, deal-breakers, and lifestyle needs.",
    suggestedQuestions: ["What are your must-have features?", "Any deal-breakers?"],
    requiredQuestions: [
      "What property features are must-haves?",
      "What are your deal-breakers?",
      "What neighborhoods or areas do you prefer?",
      "What is your budget range?",
    ],
    extractFields: ["must-haves", "deal-breakers", "preferred areas", "budget"],
    closingMessage: "Thanks — {creatorName} will use this to narrow down options.",
    recommendedDurationMinutes: 8,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "To find the right property faster, please answer a few requirement questions with my AI assistant.",
    isFeatured: false,
  }),
  tpl({
    slug: "tenant-qualification",
    name: "Tenant Qualification",
    category: "real_estate",
    description: "Screen prospective tenants before showings.",
    icon: "solar:door-linear",
    openingMessage: `${AI_OPENING} I'll ask a few questions about your rental needs and situation.`,
    objective: "Screen prospective tenants before showings.",
    systemInstructions: "Confirm move-in date, budget, household, pets, and employment.",
    suggestedQuestions: ["When do you need to move in?", "Who will be living in the unit?"],
    requiredQuestions: [
      "When do you want to move in?",
      "What is your monthly budget?",
      "How many people will live there?",
      "Do you have pets?",
      "What is your employment situation?",
    ],
    extractFields: ["move-in date", "budget", "household size", "pets", "employment"],
    closingMessage: "Thank you. {creatorName} will review and follow up.",
    recommendedDurationMinutes: 8,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Before scheduling a viewing, please complete this quick tenant qualification with my AI assistant.",
    isFeatured: false,
  }),
  tpl({
    slug: "broker-referral-intake",
    name: "Broker Referral Intake",
    category: "real_estate",
    description: "Intake details for a referred client.",
    icon: "solar:hand-shake-linear",
    openingMessage: `${AI_OPENING} I'll collect a few details about your real estate needs.`,
    objective: "Intake details for a referred real estate client.",
    systemInstructions: "Confirm referral context, client needs, and urgency.",
    suggestedQuestions: ["Who referred you?", "What are you looking to buy or sell?"],
    requiredQuestions: [
      "Who referred you?",
      "Are you buying, selling, or renting?",
      "What is your timeline?",
      "What is your budget or price range?",
    ],
    extractFields: ["referral source", "buy/sell/rent", "timeline", "budget"],
    closingMessage: "Thank you. {creatorName} will be in touch soon.",
    recommendedDurationMinutes: 6,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "You've been referred for real estate help. Please answer a few quick questions with my AI assistant.",
    isFeatured: false,
  }),

  // Customer Research
  tpl({
    slug: "customer-interview",
    name: "Customer Interview",
    category: "customer_research",
    description: "Conduct structured customer interviews without scheduling calls.",
    icon: "solar:chat-square-like-linear",
    openingMessage: `${AI_OPENING} I'd love to hear about your experience and what you're trying to accomplish.`,
    objective: "Understand customer problems, workflows, and satisfaction.",
    systemInstructions:
      "Run a thoughtful customer interview. Probe for problems, workarounds, and willingness to pay.",
    suggestedQuestions: [
      "What problem are you trying to solve?",
      "How do you handle this today?",
    ],
    requiredQuestions: [
      "What problem are you trying to solve?",
      "How do you work around it today?",
      "How often does this come up?",
      "How severe is the impact?",
      "What products do you use today?",
      "How satisfied are you with current solutions?",
      "What improvement would matter most?",
      "Would you pay for a better solution?",
      "What is your biggest objection?",
    ],
    extractFields: [
      "problem",
      "current workaround",
      "frequency",
      "severity",
      "existing products used",
      "satisfaction",
      "desired improvement",
      "willingness to pay",
      "strongest objection",
      "relevant quote/insight",
    ],
    closingMessage: "Thank you for sharing — this is really helpful. {creatorName} appreciates your time.",
    recommendedDurationMinutes: 12,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "I'd love your feedback on a few questions. Please have a short conversation with my AI assistant — no scheduling needed.",
    isFeatured: true,
  }),
  tpl({
    slug: "churn-interview",
    name: "Churn Interview",
    category: "customer_research",
    description: "Understand why a customer left.",
    icon: "solar:logout-2-linear",
    openingMessage: `${AI_OPENING} I'd like to understand your experience and what led to your decision.`,
    objective: "Understand why a customer left and what would bring them back.",
    systemInstructions: "Be empathetic. Focus on reasons, missing capabilities, and alternatives.",
    suggestedQuestions: ["What was the main reason you left?", "What would need to change for you to return?"],
    requiredQuestions: [
      "What was the main reason for leaving?",
      "What dissatisfied you most?",
      "What capability was missing?",
      "What product are you using instead?",
      "How price-sensitive was the decision?",
      "How likely are you to return?",
      "What would need to be true for you to come back?",
    ],
    extractFields: [
      "reason for leaving",
      "main dissatisfaction",
      "missing capability",
      "replacement product",
      "price sensitivity",
      "likelihood of returning",
      "condition required to return",
    ],
    closingMessage: "Thank you for the honest feedback. We appreciate it.",
    recommendedDurationMinutes: 10,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "We're sorry to see you go. If you have a moment, please share feedback with my AI assistant so we can improve.",
    isFeatured: false,
  }),
  tpl({
    slug: "product-feedback",
    name: "Product Feedback",
    category: "customer_research",
    description: "Collect structured product feedback.",
    icon: "solar:star-linear",
    openingMessage: `${AI_OPENING} I'd like to hear your thoughts on the product.`,
    objective: "Collect structured product feedback.",
    systemInstructions: "Ask about usage, likes, frustrations, and feature requests.",
    suggestedQuestions: ["What do you use most?", "What's frustrating?"],
    requiredQuestions: [
      "How do you use the product today?",
      "What do you like most?",
      "What is most frustrating?",
      "What feature would help you most?",
    ],
    extractFields: ["usage", "likes", "frustrations", "feature request"],
    closingMessage: "Thanks for the feedback — it really helps.",
    recommendedDurationMinutes: 8,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "We'd love your product feedback. Please share a few thoughts with my AI assistant when you have a moment.",
    isFeatured: false,
  }),
  tpl({
    slug: "user-research",
    name: "User Research",
    category: "customer_research",
    description: "Run qualitative user research interviews.",
    icon: "solar:magnifer-linear",
    openingMessage: `${AI_OPENING} I'm conducting a short research interview about how you work.`,
    objective: "Run qualitative user research interviews.",
    systemInstructions: "Explore workflows, pain points, and unmet needs with open follow-ups.",
    suggestedQuestions: ["Walk me through how you handle this task today.", "What slows you down?"],
    requiredQuestions: [
      "Describe your current workflow.",
      "What is the biggest pain point?",
      "What tools do you use?",
      "What would an ideal solution look like?",
    ],
    extractFields: ["workflow", "pain point", "tools", "ideal solution"],
    closingMessage: "Thank you — your input is valuable.",
    recommendedDurationMinutes: 12,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "We're doing quick user research. Please answer a few questions with my AI assistant — it only takes a few minutes.",
    isFeatured: false,
  }),
  tpl({
    slug: "beta-tester-interview",
    name: "Beta Tester Interview",
    category: "customer_research",
    description: "Collect feedback from beta testers.",
    icon: "solar:test-tube-linear",
    openingMessage: `${AI_OPENING} Thanks for beta testing — I'd like to hear about your experience.`,
    objective: "Collect structured feedback from beta testers.",
    systemInstructions: "Ask about setup, core flows, bugs, and overall impression.",
    suggestedQuestions: ["What did you try first?", "Did anything break or confuse you?"],
    requiredQuestions: [
      "What features did you try?",
      "What worked well?",
      "What didn't work or was confusing?",
      "Would you recommend this to a colleague?",
    ],
    extractFields: ["features tried", "what worked", "issues", "recommendation likelihood"],
    closingMessage: "Thanks for helping us improve the product.",
    recommendedDurationMinutes: 10,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Thanks for beta testing. Please share your experience with my AI assistant so we can improve.",
    isFeatured: false,
  }),

  // Business
  tpl({
    slug: "vendor-qualification",
    name: "Vendor Qualification",
    category: "business",
    description: "Qualify vendors before procurement calls.",
    icon: "solar:shop-linear",
    openingMessage: `${AI_OPENING} I'll ask a few questions about your offering and fit.`,
    objective: "Qualify vendors before procurement calls.",
    systemInstructions: "Confirm capabilities, pricing model, references, and timeline.",
    suggestedQuestions: ["What do you offer?", "Who are your typical customers?"],
    requiredQuestions: [
      "What service or product do you provide?",
      "What is your pricing model?",
      "Can you share relevant references or case studies?",
      "What is your typical implementation timeline?",
    ],
    extractFields: ["offering", "pricing model", "references", "timeline"],
    closingMessage: "Thank you. {creatorName} will review and follow up if there's a fit.",
    recommendedDurationMinutes: 8,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Before we schedule a procurement call, please answer a few qualification questions with my AI assistant.",
    isFeatured: false,
  }),
  tpl({
    slug: "partnership-discovery",
    name: "Partnership Discovery",
    category: "business",
    description: "Explore partnership fit and goals.",
    icon: "solar:hand-stars-linear",
    openingMessage: `${AI_OPENING} I'd like to explore whether a partnership could make sense.`,
    objective: "Explore partnership fit and mutual goals.",
    systemInstructions: "Understand partner goals, audience overlap, and collaboration ideas.",
    suggestedQuestions: ["What kind of partnership are you looking for?", "Who is your audience?"],
    requiredQuestions: [
      "What partnership model are you interested in?",
      "What is your target audience or customer base?",
      "What would success look like for you?",
    ],
    extractFields: ["partnership model", "audience", "success criteria"],
    closingMessage: "Thanks — {creatorName} will review and reach out.",
    recommendedDurationMinutes: 10,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "I'd like to explore a potential partnership. Please answer a few questions with my AI assistant first.",
    isFeatured: false,
  }),
  tpl({
    slug: "client-intake",
    name: "Client Intake",
    category: "business",
    description: "Collect client details before onboarding.",
    icon: "solar:folder-with-files-linear",
    openingMessage: `${AI_OPENING} I'll gather a few details to prepare for our work together.`,
    objective: "Collect client details before onboarding.",
    systemInstructions: "Gather goals, scope, stakeholders, and timeline.",
    suggestedQuestions: ["What are you hoping to achieve?", "Who is involved on your side?"],
    requiredQuestions: [
      "What is the primary goal of this engagement?",
      "What is the scope or deliverables you expect?",
      "Who are the key stakeholders?",
      "What is your timeline?",
    ],
    extractFields: ["goal", "scope", "stakeholders", "timeline"],
    closingMessage: "Thank you. {creatorName} will use this to prepare onboarding.",
    recommendedDurationMinutes: 10,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Before we kick off, please complete this short intake with my AI assistant so I can prepare properly.",
    isFeatured: false,
  }),
  tpl({
    slug: "employee-check-in",
    name: "Employee Check-in",
    category: "business",
    description: "Run structured employee check-ins.",
    icon: "solar:heart-pulse-linear",
    openingMessage: `${AI_OPENING} I'd like to check in on how things are going for you.`,
    objective: "Run structured employee check-ins.",
    systemInstructions: "Ask about workload, blockers, morale, and support needed.",
    suggestedQuestions: ["How are things going this week?", "Any blockers I should know about?"],
    requiredQuestions: [
      "How is your workload?",
      "What is going well?",
      "What is challenging right now?",
      "What support would help?",
    ],
    extractFields: ["workload", "wins", "challenges", "support needed"],
    closingMessage: "Thanks for sharing. {creatorName} will review this check-in.",
    recommendedDurationMinutes: 8,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Quick check-in — please share how things are going with my AI assistant when you have a few minutes.",
    isFeatured: false,
  }),

  // Research
  tpl({
    slug: "voice-survey",
    name: "Voice Survey",
    category: "research",
    description: "Run a voice-based survey interview.",
    icon: "solar:microphone-3-linear",
    openingMessage: `${AI_OPENING} I'll ask you a short set of research questions by voice.`,
    objective: "Run a voice-based survey interview.",
    systemInstructions: "Ask questions clearly one at a time. Confirm answers before moving on.",
    suggestedQuestions: ["Please answer each question as clearly as you can."],
    requiredQuestions: [
      "Please share your overall opinion on the topic.",
      "What influenced your view the most?",
      "Anything else you'd like to add?",
    ],
    extractFields: ["overall opinion", "key influence", "additional comments"],
    closingMessage: "Thank you for completing this survey.",
    recommendedDurationMinutes: 8,
    allowDynamicFollowups: false,
    allowRespondentQuestions: false,
    shareMessage:
      "Please complete this short voice survey with my AI assistant — it only takes a few minutes.",
    isFeatured: false,
  }),
  tpl({
    slug: "qualitative-interview",
    name: "Qualitative Interview",
    category: "research",
    description: "Run structured qualitative interviews at scale.",
    icon: "solar:notes-linear",
    openingMessage: `${AI_OPENING} I'm conducting a short qualitative research interview.`,
    objective: "Run structured qualitative interviews at scale.",
    systemInstructions: "Use open-ended questions and probe for examples and emotions.",
    suggestedQuestions: ["Tell me about your experience.", "Can you give a specific example?"],
    requiredQuestions: [
      "What is your experience with this topic?",
      "Can you share a specific example?",
      "What matters most to you about this?",
    ],
    extractFields: ["experience", "example", "priorities", "insights"],
    closingMessage: "Thank you for participating in this research.",
    recommendedDurationMinutes: 12,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "We're running quick research interviews. Please answer a few questions with my AI assistant.",
    isFeatured: true,
  }),
  tpl({
    slug: "market-research-interview",
    name: "Market Research Interview",
    category: "research",
    description: "Gather market insights from participants.",
    icon: "solar:chart-2-linear",
    openingMessage: `${AI_OPENING} I'd like to ask a few market research questions.`,
    objective: "Gather market insights from participants.",
    systemInstructions: "Explore preferences, alternatives, and buying behavior.",
    suggestedQuestions: ["What options have you considered?", "What factors matter most in your decision?"],
    requiredQuestions: [
      "What alternatives are you aware of?",
      "What factors influence your choice?",
      "How price-sensitive are you?",
    ],
    extractFields: ["alternatives", "decision factors", "price sensitivity"],
    closingMessage: "Thank you for your input.",
    recommendedDurationMinutes: 10,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "We're doing brief market research. Please answer a few questions with my AI assistant.",
    isFeatured: false,
  }),

  // General
  tpl({
    slug: "ask-someone-questions",
    name: "Ask Someone Questions",
    category: "general",
    description: "Ask someone a set of questions without scheduling a call.",
    icon: "solar:question-circle-linear",
    openingMessage: `${AI_OPENING} I have a few questions for you.`,
    objective: "Collect answers to a specific set of questions.",
    systemInstructions: "Ask the required questions clearly and confirm understanding.",
    suggestedQuestions: ["Is there anything else you'd like to add?"],
    requiredQuestions: ["Please answer the questions {creatorName} needs."],
    extractFields: ["answers"],
    closingMessage: "Thank you. {creatorName} will review your answers.",
    recommendedDurationMinutes: 8,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "I have a few questions for you. Please answer them with my AI assistant — no call needed.",
    isFeatured: false,
  }),
  tpl({
    slug: "collect-feedback",
    name: "Collect Feedback",
    category: "general",
    description: "Collect open feedback on any topic.",
    icon: "solar:chat-line-linear",
    openingMessage: `${AI_OPENING} I'd love to hear your feedback.`,
    objective: "Collect open feedback on any topic.",
    systemInstructions: "Encourage honest feedback. Probe for specifics.",
    suggestedQuestions: ["What went well?", "What could be better?"],
    requiredQuestions: [
      "What is your overall feedback?",
      "What should we keep doing?",
      "What should we improve?",
    ],
    extractFields: ["overall feedback", "keep doing", "improve"],
    closingMessage: "Thank you for the feedback.",
    recommendedDurationMinutes: 6,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "I'd appreciate your feedback. Please share a few thoughts with my AI assistant.",
    isFeatured: false,
  }),
  tpl({
    slug: "custom-proxy",
    name: "Custom Proxy",
    category: "general",
    description: "Tell the AI what information you need and let it design the conversation.",
    icon: "solar:magic-stick-3-linear",
    openingMessage: `${AI_OPENING} I'll ask the questions {creatorName} needs answered.`,
    objective: "Collect the information the creator needs through a tailored conversation.",
    systemInstructions:
      "Conduct a focused conversation to gather the information the creator needs. Ask clear follow-ups.",
    suggestedQuestions: [],
    requiredQuestions: [],
    extractFields: [],
    closingMessage: "Thank you. {creatorName} will review what you shared.",
    recommendedDurationMinutes: 10,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Please have a quick conversation with my AI assistant. It will ask a few questions on my behalf.",
    isFeatured: true,
  }),

  // Coaching & Advisory
  tpl({
    slug: "pre-session-brief",
    name: "Pre-Session Brief",
    category: "coaching_advisory",
    description:
      "Collect the important updates, problems, decisions, and questions before your coaching session so you can spend live time where it matters.",
    icon: "solar:clipboard-check-linear",
    openingMessage: `${COACHING_OPENING} Before your upcoming session, I'd like to collect a few updates so {creatorName} can understand what's changed and what you'd most like to work through. I'm an AI, and a summary of this conversation will be shared with {creatorName}.`,
    objective:
      "Prepare a concise briefing for the coach before the live session.",
    systemInstructions: COACHING_SESSION_ASSISTANT_INSTRUCTIONS,
    suggestedQuestions: [
      "What has changed since the last session?",
      "What progress have you made?",
      "What did you intend to accomplish?",
      "What remains unfinished?",
      "What got in the way?",
      "What is taking most of your attention right now?",
      "Is there an important problem or decision you need help with?",
      "What options are you considering?",
      "What are you uncertain about?",
      "Is there anything your coach needs to know before the session?",
      "What would make the upcoming session valuable?",
    ],
    requiredQuestions: [
      "What has changed since your last session?",
      "What progress have you made on what you intended to accomplish?",
      "What remains unfinished, and what got in the way?",
      "What is taking most of your attention right now?",
      "Is there an important problem or decision you want to work through?",
      "What would make the upcoming session valuable?",
    ],
    extractFields: [
      "majorUpdates",
      "progress",
      "unfinishedCommitments",
      "obstacles",
      "currentPriority",
      "importantProblems",
      "decisions",
      "optionsBeingConsidered",
      "uncertainties",
      "coachShouldKnow",
      "desiredSessionOutcome",
    ],
    closingMessage:
      "Thank you. I've captured your updates so {creatorName} can review them before your session.",
    recommendedDurationMinutes: 15,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Before our next session, please complete this short AI check-in. It will collect the main updates, issues, and decisions you'd like us to discuss so we can use our live time more effectively.",
    proxyRole: "session_assistant",
    isFeatured: true,
  }),
  tpl({
    slug: "weekly-progress-check-in",
    name: "Weekly Progress Check-in",
    category: "coaching_advisory",
    description: "Collect progress, obstacles, and changing priorities.",
    icon: "solar:chart-linear",
    openingMessage: `${COACHING_OPENING} {creatorName} asked me to collect a quick progress update before your next session. I'm an AI, and a summary of this conversation will be shared with {creatorName}.`,
    objective:
      "Collect a concise progress report between coaching sessions.",
    systemInstructions: COACHING_SESSION_ASSISTANT_INSTRUCTIONS,
    suggestedQuestions: [
      "What progress have you made since the previous session?",
      "Which commitments did you complete?",
      "Which commitments did you miss?",
      "What obstacles came up?",
      "Have your priorities changed?",
      "Is there an emerging problem?",
      "What is your next action?",
    ],
    requiredQuestions: [
      "What progress have you made since your last session?",
      "Which commitments did you complete or miss?",
      "What obstacles came up?",
      "Have your priorities changed?",
      "What is your next action?",
    ],
    extractFields: [
      "progressSinceLastSession",
      "commitmentsCompleted",
      "commitmentsMissed",
      "obstacles",
      "changedPriorities",
      "emergingProblem",
      "nextAction",
    ],
    closingMessage: "Thank you. {creatorName} will review your progress update.",
    recommendedDurationMinutes: 8,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Please send me a quick progress update through my AI Proxy before our next session. It should only take a few minutes.",
    proxyRole: "session_assistant",
    isFeatured: false,
  }),
  tpl({
    slug: "commitment-check-in",
    name: "Commitment Check-in",
    category: "coaching_advisory",
    description: "Track whether agreed actions were completed.",
    icon: "solar:checklist-minimalistic-linear",
    openingMessage: `${COACHING_OPENING} {creatorName} asked me to check in on a commitment from your last session. I'm an AI, and a summary will be shared with {creatorName}.`,
    objective: "Track whether the client followed through on an agreed action.",
    systemInstructions:
      "Focus on the previous commitment and whether it was completed. Ask what prevented completion if it was not done. Do not turn the discussion into psychological analysis. Do not use labels like self-sabotaging.",
    suggestedQuestions: [
      "What was the previous commitment?",
      "Was it completed, partially completed, or not completed?",
      "What was the result?",
      "What obstacle came up?",
      "Have circumstances changed?",
      "What is the planned next action?",
      "What is the target date?",
    ],
    requiredQuestions: [
      "What commitment are we checking in on?",
      "Was it completed, partially completed, or not completed?",
      "If not completed, what prevented it?",
      "What is the planned next action and target date?",
    ],
    extractFields: [
      "previousCommitment",
      "completionStatus",
      "result",
      "obstacle",
      "changedCircumstances",
      "plannedNextAction",
      "targetDate",
    ],
    closingMessage: "Thank you. {creatorName} will review your commitment check-in.",
    recommendedDurationMinutes: 6,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Please complete this short check-in so we can review the commitment from our last session and any obstacles that came up.",
    proxyRole: "session_assistant",
    isFeatured: false,
  }),
  tpl({
    slug: "post-session-reflection",
    name: "Post-Session Reflection",
    category: "coaching_advisory",
    description: "Capture insights and commitments after the call.",
    icon: "solar:notes-linear",
    openingMessage: `${COACHING_OPENING} {creatorName} asked me to capture what you took away from your recent coaching session. I'm an AI, and a summary will be shared with {creatorName}.`,
    objective:
      "Capture what the client took away from a live human coaching session.",
    systemInstructions: COACHING_SESSION_ASSISTANT_INSTRUCTIONS,
    suggestedQuestions: [
      "What was your biggest takeaway?",
      "What became clearer?",
      "What decision did you make?",
      "What did you commit to doing?",
      "What is the first action?",
      "When will you do it?",
      "What could interfere with that commitment?",
    ],
    requiredQuestions: [
      "What was your biggest takeaway from the session?",
      "What became clearer?",
      "What did you commit to doing?",
      "What is the first action and when will you do it?",
      "What could interfere with that commitment?",
    ],
    extractFields: [
      "biggestTakeaway",
      "whatBecameClearer",
      "decisions",
      "commitments",
      "firstAction",
      "targetDate",
      "possibleObstacles",
    ],
    closingMessage: "Thank you. {creatorName} will review your reflection.",
    recommendedDurationMinutes: 8,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "After our session, please share a quick reflection through my AI Proxy so I can capture your key takeaways and commitments.",
    proxyRole: "session_assistant",
    isFeatured: false,
  }),
  tpl({
    slug: "executive-debrief",
    name: "Executive Debrief",
    category: "coaching_advisory",
    description: "Collect context after an important meeting or leadership event.",
    icon: "solar:buildings-2-linear",
    openingMessage: `${COACHING_OPENING} {creatorName} asked me to collect a debrief while the details are still fresh. I'm an AI, and a summary will be shared with {creatorName}.`,
    objective:
      "Collect information after an important executive event for the coach to review.",
    systemInstructions:
      "Collect a factual debrief. Do not conduct executive coaching. Do not give HR or employment-law advice. Ask clarifying follow-ups when answers are vague.",
    suggestedQuestions: [
      "What happened?",
      "What outcome did you want?",
      "What actually happened?",
      "What went well?",
      "What went poorly?",
      "What surprised you?",
      "What decision or follow-up is now required?",
      "What would you like to discuss with your coach?",
    ],
    requiredQuestions: [
      "What happened?",
      "What outcome did you want, and what actually happened?",
      "What went well and what went poorly?",
      "What surprised you?",
      "What decision or follow-up is now required?",
      "What would you like to discuss with your coach?",
    ],
    extractFields: [
      "whatHappened",
      "intendedOutcome",
      "actualOutcome",
      "whatWentWell",
      "whatWentPoorly",
      "surprises",
      "requiredFollowUp",
      "topicsForCoaching",
    ],
    closingMessage: "Thank you. {creatorName} will review your debrief.",
    recommendedDurationMinutes: 10,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Use this short AI Proxy conversation to capture what happened while it's still fresh. I'll review the summary before our next session.",
    proxyRole: "session_assistant",
    isFeatured: false,
  }),
  tpl({
    slug: "decision-preparation",
    name: "Decision Preparation",
    category: "coaching_advisory",
    description: "Organize a difficult decision before discussing it live.",
    icon: "solar:scale-linear",
    openingMessage: `${COACHING_OPENING} {creatorName} asked me to help you organize a decision before you discuss it together. I'm an AI, and a summary will be shared with {creatorName}.`,
    objective:
      "Help the client organize a decision before discussing it with the coach.",
    systemInstructions:
      "Help the client organize the decision, options, assumptions, and trade-offs. Ask clarifying questions. Do not tell the client what decision they should make.",
    suggestedQuestions: [
      "What decision are you facing?",
      "What outcome do you want?",
      "What options are available?",
      "What facts do you know?",
      "What assumptions are you making?",
      "What are the major trade-offs?",
      "What are the risks?",
      "How reversible is the decision?",
      "What information is missing?",
      "What is your current preference?",
      "What do you want from your coach?",
    ],
    requiredQuestions: [
      "What decision are you facing?",
      "What outcome do you want?",
      "What options are you considering?",
      "What are the major trade-offs and risks?",
      "What do you want from your coach on this decision?",
    ],
    extractFields: [
      "decision",
      "desiredOutcome",
      "options",
      "knownFacts",
      "assumptions",
      "tradeOffs",
      "risks",
      "reversibility",
      "missingInformation",
      "currentPreference",
      "whatTheyWantFromCoach",
    ],
    closingMessage:
      "I've organized the main decision, options, assumptions, and trade-offs so you can discuss them with {creatorName}.",
    recommendedDurationMinutes: 10,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Before our next session, please use my AI Proxy to organize the decision you'd like us to discuss.",
    proxyRole: "session_assistant",
    isFeatured: false,
  }),
  tpl({
    slug: "founder-check-in",
    name: "Founder Check-in",
    category: "coaching_advisory",
    description: "Surface priorities, bottlenecks, decisions, and execution issues.",
    icon: "solar:rocket-2-linear",
    openingMessage: `${COACHING_OPENING} Before your upcoming advisory session, I'd like to collect a few updates for {creatorName}. I'm an AI, and a summary will be shared with {creatorName}.`,
    objective:
      "Prepare a founder for a coaching or advisory session with better context.",
    systemInstructions:
      "Collect founder context for the human advisor. Do not provide generic startup advice unless the creator's instructions explicitly permit answering recipient questions. Primary objective: give the human advisor better context.",
    suggestedQuestions: [
      "What is your current number-one priority?",
      "What is your biggest bottleneck?",
      "What major decision are you facing?",
      "What progress have you made since the last session?",
      "What commitment did you miss?",
      "What resource constraint is most limiting you?",
      "Is there a team issue?",
      "What is distracting you?",
      "What has changed?",
      "What outcome do you want from the next session?",
    ],
    requiredQuestions: [
      "What is your current number-one priority?",
      "What is your biggest bottleneck or constraint?",
      "What major decision are you facing?",
      "What progress have you made since the last session?",
      "What outcome do you want from the next session?",
    ],
    extractFields: [
      "currentPriority",
      "biggestBottleneck",
      "majorDecision",
      "progressSinceLastSession",
      "missedCommitment",
      "resourceConstraint",
      "teamIssue",
      "distraction",
      "whatChanged",
      "desiredSessionOutcome",
    ],
    closingMessage: "Thank you. {creatorName} will review your founder check-in.",
    recommendedDurationMinutes: 10,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Before our next advisory session, please complete this short founder check-in through my AI Proxy.",
    proxyRole: "session_assistant",
    isFeatured: false,
  }),
  tpl({
    slug: "leadership-situation-brief",
    name: "Leadership Situation Brief",
    category: "coaching_advisory",
    description: "Prepare a management or team issue for discussion.",
    icon: "solar:users-group-two-rounded-linear",
    openingMessage: `${COACHING_OPENING} Before your upcoming session, I'd like to collect context on a leadership situation for {creatorName}. I'm an AI, and a summary will be shared with {creatorName}.`,
    objective:
      "Prepare a leadership issue for discussion with a coach.",
    systemInstructions:
      "Collect context on the leadership situation. Do not give definitive HR or employment-law advice. Do not diagnose people involved.",
    suggestedQuestions: [
      "What is the situation?",
      "Who is involved?",
      "What outcome do you want?",
      "What has happened so far?",
      "What actions have you already taken?",
      "What options are you considering?",
      "What is your main concern?",
      "What do you want help thinking through?",
    ],
    requiredQuestions: [
      "What is the leadership situation?",
      "Who is involved and what outcome do you want?",
      "What has happened so far and what actions have you taken?",
      "What options are you considering?",
      "What do you want help thinking through?",
    ],
    extractFields: [
      "situation",
      "peopleInvolved",
      "desiredOutcome",
      "whatHasHappened",
      "actionsTaken",
      "currentOptions",
      "concern",
      "helpNeeded",
    ],
    closingMessage: "Thank you. {creatorName} will review your leadership brief.",
    recommendedDurationMinutes: 10,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Before our next session, please use my AI Proxy to brief me on the leadership situation you'd like to discuss.",
    proxyRole: "session_assistant",
    isFeatured: false,
  }),
  tpl({
    slug: "custom-coach-intake",
    name: "Custom Coach Intake",
    category: "coaching_advisory",
    description: "Create your own pre-session or check-in process.",
    icon: "solar:settings-linear",
    openingMessage: `${COACHING_OPENING} {creatorName} asked me to run a custom check-in with you. I'm an AI, and a summary will be shared with {creatorName}.`,
    objective:
      "Collect information through a creator-defined check-in process.",
    systemInstructions: COACHING_CUSTOM_INTAKE_INSTRUCTIONS,
    suggestedQuestions: [],
    requiredQuestions: [],
    extractFields: [],
    closingMessage: "Thank you. {creatorName} will review what you shared.",
    recommendedDurationMinutes: 30,
    allowDynamicFollowups: true,
    allowRespondentQuestions: true,
    shareMessage:
      "Please complete this custom check-in through my AI Proxy before our next session.",
    proxyRole: "custom",
    isFeatured: false,
  }),
];

export const FEATURED_LANDING_SLUGS = [
  "lead-qualification",
  "candidate-pre-screening",
  "customer-interview",
  "buyer-qualification",
  "qualitative-interview",
  "custom-proxy",
] as const;

export function getTemplateBySlug(slug: string): ProxyTemplate | undefined {
  return PROXY_TEMPLATES.find((t) => t.slug === slug && t.isActive);
}

export function getTemplatesByCategory(): Record<ProxyTemplateCategory, ProxyTemplate[]> {
  const grouped = {} as Record<ProxyTemplateCategory, ProxyTemplate[]>;
  for (const category of Object.keys(TEMPLATE_CATEGORY_LABELS) as ProxyTemplateCategory[]) {
    grouped[category] = PROXY_TEMPLATES.filter((t) => t.category === category && t.isActive);
  }
  return grouped;
}

export function getFeaturedTemplates(): ProxyTemplate[] {
  return FEATURED_LANDING_SLUGS.map((slug) => getTemplateBySlug(slug)).filter(
    (t): t is ProxyTemplate => !!t,
  );
}

export function getActiveTemplates(): ProxyTemplate[] {
  return PROXY_TEMPLATES.filter((t) => t.isActive);
}

export function isCoachingAdvisorySlug(slug: string): boolean {
  const template = PROXY_TEMPLATES.find((t) => t.slug === slug);
  return template?.category === "coaching_advisory";
}

export function getCoachingAdvisoryTemplates(): ProxyTemplate[] {
  return PROXY_TEMPLATES.filter((t) => t.category === "coaching_advisory" && t.isActive);
}
